import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FiberManualRecord,
  OpenInNew as OpenInNewIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Router as RouterIcon,
  LocationPin as LocationPinIcon,
} from "@mui/icons-material";
import {
  formatLastUpdated,
  formatResponseCode,
  getBatteryColor,
  getBatteryIcon,
  getConnectionColor,
  getConnectionIcon,
  getConnectionTooltip,
  getRobotStatus,
} from "./deviceStatusUtils";
import DeviceManufacturerStrip from "./DeviceManufacturerStrip";

function DeviceStatusTable({
  devices,
  urlPrefix = "/device/",
  favoritesMap = {},
  onToggleFavorite,
}) {
  const navigate = useNavigate();

  return (
    <TableContainer
      component={Paper}
      sx={{
        borderRadius: 3,
        boxShadow: 3,
        overflowX: "auto",
        overflowY: "hidden",
      }}
    >
      <Table
        size="small"
        aria-label="device status dashboard"
        sx={{
          "& .MuiTableCell-root": {
            px: { xs: 0.75, sm: 2 },
            py: { xs: 0.5, sm: 1 },
          },
          "& .MuiTableCell-head": {
            fontSize: { xs: "0.68rem", sm: "0.875rem" },
            whiteSpace: "nowrap",
          },
        }}
      >
        <TableHead>
          <TableRow sx={{ backgroundColor: "grey.100" }}>
            <TableCell sx={{ fontWeight: 700 }}>Device</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>State</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Cleaning Step</TableCell>
            <TableCell sx={{ fontWeight: 700, width: 110 }}>Battery</TableCell>
            <TableCell sx={{ fontWeight: 700, width: 90 }}>Online</TableCell>
            <TableCell sx={{ fontWeight: 700, width: 150 }}>
              Overall Status
            </TableCell>
            <TableCell sx={{ fontWeight: 700, width: 56 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {devices.map((device) => {
            const robotStatus = getRobotStatus(device);
            const isStale = robotStatus.isStale;
            const isOnline =
              device.connected === 1 || device.connected === true;
            const isFavorite = Boolean(favoritesMap[device.rc_device_id]);
            const batteryLevel = device.battery_level || 0;
            const BatteryIcon = getBatteryIcon(batteryLevel, device);
            const batteryColor = getBatteryColor(
              batteryLevel,
              isOnline,
              isStale,
            );
            const ConnectionIcon = getConnectionIcon(isOnline);
            const connectionColor = getConnectionColor(isOnline, isStale);
            const connectionTooltip = getConnectionTooltip(isOnline, isStale);
            const errorCodes = robotStatus.responseCodes || [];

            return (
              <TableRow
                key={device.device_pk_id || device.pk_id || device.rc_device_id}
                hover
                sx={{
                  cursor: "pointer",
                  backgroundColor: robotStatus.isStale
                    ? "rgba(158, 158, 158, 0.08)"
                    : robotStatus.color === "#f44336"
                      ? "rgba(244, 67, 54, 0.045)"
                      : robotStatus.color === "#ff9800"
                        ? "rgba(255, 152, 0, 0.045)"
                        : "rgba(76, 175, 80, 0.03)",
                }}
                onClick={() => navigate(`${urlPrefix}${device.rc_device_id}`)}
              >
                <TableCell>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <DeviceManufacturerStrip
                        manufacturers={[device.manufacturer] || []}
                        size={18}
                      />
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: "0.72rem", sm: "0.875rem" },
                        }}
                      >
                        {device.name || "Unnamed Device"}
                        <IconButton
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            if (typeof onToggleFavorite === "function") {
                              onToggleFavorite(device.rc_device_id);
                            }
                          }}
                          sx={{ ml: 0.5, p: { xs: 0.15, sm: 0.25 } }}
                          aria-label={
                            isFavorite
                              ? "remove device from favourites"
                              : "add device to favourites"
                          }
                        >
                          {isFavorite ? (
                            <StarIcon
                              sx={{
                                fontSize: { xs: 13, sm: 16 },
                                color: "#f9a825",
                              }}
                            />
                          ) : (
                            <StarBorderIcon
                              sx={{
                                fontSize: { xs: 13, sm: 16 },
                                color: "text.secondary",
                              }}
                            />
                          )}
                        </IconButton>
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: { xs: "0.62rem", sm: "0.75rem" } }}
                    >
                      UUID: {device.rc_device_id}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: { xs: "0.62rem", sm: "0.75rem" } }}
                    >
                      S/N: {device.uk_device_id}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: { xs: "0.62rem", sm: "0.75rem" } }}
                    >
                      Account: {device.account || "N/A"}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ verticalAlign: "top" }}>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" }, mb: 1 }}
                  >
                    {device.customer_name || "Unassigned"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
                  >
                    <LocationPinIcon
                      sx={{
                        fontSize: { xs: 14, sm: 18 },
                        verticalAlign: "middle",
                        marginRight: 0.5,
                      }}
                    />{" "}
                    {device.location_name || "Unknown"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
                  >
                    {device.operating_state || "Unknown"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
                  >
                    {device.cleaning_step || "N/A"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Tooltip
                    title={
                      isStale
                        ? "Battery level unknown (stale data)"
                        : `Battery: ${batteryLevel}%`
                    }
                    arrow
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                      <BatteryIcon
                        sx={{
                          fontSize: { xs: 14, sm: 18 },
                          color: batteryColor,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          color: batteryColor,
                          fontSize: { xs: "0.7rem", sm: "0.875rem" },
                        }}
                      >
                        {!isStale ? `${batteryLevel}%` : "--"}
                      </Typography>
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title={connectionTooltip} arrow>
                    <ConnectionIcon
                      sx={{
                        fontSize: { xs: 14, sm: 18 },
                        color: connectionColor,
                      }}
                    />
                  </Tooltip>
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                  >
                    <RouterIcon
                      sx={{
                        fontSize: { xs: 14, sm: 18 },
                        color: "inherit",
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
                    >
                      {device.router_id || "none"}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Tooltip
                    title={
                      robotStatus.isStale ? (
                        `No data since ${formatLastUpdated(robotStatus.lastUpdated)}`
                      ) : robotStatus.status === "Offline" ? (
                        `Last log: ${formatLastUpdated(device.log_timestamp)}`
                      ) : robotStatus.status === "Error" &&
                        errorCodes.length > 0 ? (
                        <Box
                          component="span"
                          sx={{
                            display: "block",
                            whiteSpace: "pre-line",
                          }}
                        >
                          {`${errorCodes.map(formatResponseCode).join("\n")}`}
                        </Box>
                      ) : (
                        robotStatus.status
                      )
                    }
                    arrow
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <FiberManualRecord
                        sx={{
                          fontSize: { xs: 13, sm: 16 },
                          color: robotStatus.color,
                          opacity: robotStatus.isStale ? 0.6 : 1,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
                      >
                        {robotStatus.status}
                      </Typography>
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Open in new tab" arrow>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        window.open(
                          `${urlPrefix}${device.rc_device_id}`,
                          "_blank",
                          "noopener,noreferrer",
                        );
                      }}
                      aria-label="open device in new tab"
                      sx={{ p: { xs: 0.25, sm: 0.5 } }}
                    >
                      <OpenInNewIcon
                        sx={{
                          fontSize: { xs: 14, sm: 18 },
                          color: "text.secondary",
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default React.memo(DeviceStatusTable);
